import React, { useState, useEffect } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEye,
  faEyeSlash,
  faMobileAlt,
  faCreditCard,
} from "@fortawesome/free-solid-svg-icons"; // Import icons
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard } from "../App";
import { Oval } from "react-loader-spinner"; // Import loader
import { RoleGuard } from "../App";
import { StandardURL } from "../App";

const DepositsPage = () => {
  const [balance, setBalance] = useState(null);
  const [showBalance, setShowBalance] = useState(true);
  const [amount, setAmount] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [depositAmount, setDepositAmount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [webUrl, setWebUrl] = useState("");
  const [showIframe, setShowIframe] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState("ipay"); // State to handle the selected payment method

  const token = localStorage.getItem("token");
  const propertyId = localStorage.getItem("selectedPropertyId");
  const navigate = useNavigate();

  useEffect(() => {
    if (token && propertyId) {
      axios
        .get(
          `${StandardURL}/iNyumba/balance/owner/${propertyId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        )
        .then((response) => {
          setBalance(
            response.data.response.length > 0
              ? response.data.response[0].balance
              : 0
          );
        })
        .catch((error) => {
          console.error("Error fetching balance:", error);
        });
    }
  }, [token, propertyId]);

  useEffect(() => {
    if (paymentMethod === "ipay") {
      setDepositAmount((amount * 0.98).toFixed(2)); // 2% charge for iPay
    } else if (paymentMethod === "mpesa") {
      setDepositAmount((amount * 0.99).toFixed(2)); // 1% charge for M-Pesa
    }
  }, [amount, paymentMethod]);

  const toggleShowBalance = () => {
    setShowBalance(!showBalance);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    const apiUrl =
      paymentMethod === "ipay"
        ? `${StandardURL}/iNyumba/initiatepayment/${propertyId}`
        : `${StandardURL}/iNyumba/user/mpesapayment/landlordtopupinitiatestk/${propertyId}`; // Adjust API endpoint for M-Pesa

    try {
      const response = await axios.post(
        apiUrl,
        {
          amount,
          phone_number: phoneNumber,
          email,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.data.success) {
        if (paymentMethod === "ipay") {
          setWebUrl(response.data.response); // Only for iPay, set the webUrl
          toast.success("Payment initiated successfully!");
          setShowIframe(true); // Display iframe for iPay
        } else {
          if (
            response.data.response === "Kindly input your pin on mpesa pop up"
          ) {
            toast.info("Kindly input your pin on mpesa pop up");
            setShowIframe(false); // No iframe for M-Pesa
          } else {
            toast.success("M-Pesa payment initiated successfully!");
            setShowIframe(false); // No iframe for M-Pesa
          }
        }
      } else {
        toast.error(response.data.response || "Payment initiation failed!");
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      toast.error(error.response.data.response);
    }
    setIsLoading(false);
  };

  const handleCloseIframe = () => {
    setShowIframe(false); // Hide the iframe
    setWebUrl(""); // Clear the payment URL
    navigate("/landlord/deposits");
  };

  return (
    <RoleGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-100 p-4 sm:p-6 lg:p-8">
          <div className="max-w-3xl mx-auto bg-white p-6 sm:p-8 rounded-xl shadow-2xl border border-gray-200">
            {/* --- Header Section with Balance --- */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8 gap-4">
              <h1 className="text-4xl font-extrabold text-gray-900 tracking-tight flex items-center">
                Make a Deposit 💰
              </h1>
              <div className="flex items-center bg-gray-100 px-4 py-2 rounded-lg shadow-inner">
                <p className="mr-3 text-lg font-semibold text-gray-700">
                  Balance:{" "}
                  <span className="font-mono text-blue-600">
                    {balance !== null
                      ? showBalance
                        ? `Ksh ${parseFloat(balance).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                        : "*******"
                      : "Ksh 0.00"}
                  </span>
                </p>
                <FontAwesomeIcon
                  icon={showBalance ? faEyeSlash : faEye}
                  className="text-xl cursor-pointer text-gray-500 hover:text-blue-600 transition-colors duration-200"
                  onClick={toggleShowBalance}
                  title={showBalance ? "Hide balance" : "Show balance"}
                />
              </div>
            </div>

            <p className="text-gray-600 mb-8 max-w-2xl mx-auto text-center">
              Choose your preferred payment method and enter the required details to top up your account balance securely.
            </p>

            {/* --- Deposit Form Card --- */}
            <div className="bg-white rounded-lg p-6 sm:p-8 border border-gray-100 shadow-md">
              <h2 className="text-2xl font-bold text-gray-800 mb-6 border-b pb-4">
                Deposit Details
              </h2>

              {/* Payment Method Selection */}
              <div className="mb-6">
                <label className="block text-gray-800 text-base font-semibold mb-3">
                  Select Payment Method:
                </label>
                <div className="flex rounded-lg shadow-sm overflow-hidden border border-gray-200">
                  <button
                    type="button"
                    className={`flex-1 px-6 py-3 text-center text-lg font-medium transition-all duration-300 ease-in-out
                      ${
                        paymentMethod === "ipay"
                          ? "bg-blue-600 text-white shadow-lg z-10"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      } rounded-l-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                    onClick={() => setPaymentMethod("ipay")}
                  >
                    iPay
                  </button>
                  <button
                    type="button"
                    className={`flex-1 px-6 py-3 text-center text-lg font-medium transition-all duration-300 ease-in-out
                      ${
                        paymentMethod === "mpesa"
                          ? "bg-green-600 text-white shadow-lg z-10"
                          : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      } rounded-r-lg focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2`}
                    onClick={() => setPaymentMethod("mpesa")}
                  >
                    M-Pesa
                  </button>
                </div>
              </div>

              <form onSubmit={handleSubmit}>
                {paymentMethod === "ipay" && (
                  <>
                    <div className="mb-5">
                      <label htmlFor="email-ipay" className="block text-gray-800 text-sm font-semibold mb-2">
                        Email:
                      </label>
                      <input
                        type="email"
                        id="email-ipay"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 placeholder-gray-400"
                        placeholder="your@example.com"
                        required
                      />
                    </div>
                    <div className="mb-5">
                      <label htmlFor="phone-ipay" className="block text-gray-800 text-sm font-semibold mb-2">
                        Phone Number:
                      </label>
                      <input
                        type="text"
                        id="phone-ipay"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 placeholder-gray-400"
                        placeholder="e.g., 2547XXXXXXXX or 07XXXXXXXX"
                        required
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Use format 2547XXXXXXXX or 07XXXXXXXX (do not add '+').
                      </p>
                    </div>
                    <div className="mb-6">
                      <label htmlFor="amount-ipay" className="block text-gray-800 text-sm font-semibold mb-2">
                        Amount (Ksh):
                      </label>
                      <input
                        type="number"
                        id="amount-ipay"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 placeholder-gray-400"
                        placeholder="e.g., 1000"
                        min="1" // Ensure positive amounts
                        required
                      />
                    </div>
                  </>
                )}

                {paymentMethod === "mpesa" && (
                  <>
                    <div className="mb-5">
                      <label htmlFor="phone-mpesa" className="block text-gray-800 text-sm font-semibold mb-2">
                        Phone Number:
                      </label>
                      <input
                        type="text"
                        id="phone-mpesa"
                        value={phoneNumber}
                        onChange={(e) => setPhoneNumber(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 placeholder-gray-400"
                        placeholder="e.g., 2547XXXXXXXX or 07XXXXXXXX"
                        required
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        Use format 2547XXXXXXXX or 07XXXXXXXX (do not add '+').
                      </p>
                    </div>
                    <div className="mb-6">
                      <label htmlFor="amount-mpesa" className="block text-gray-800 text-sm font-semibold mb-2">
                        Amount (Ksh):
                      </label>
                      <input
                        type="number"
                        id="amount-mpesa"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        className="w-full px-4 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900 placeholder-gray-400"
                        placeholder="e.g., 500"
                        min="1" // Ensure positive amounts
                        required
                      />
                    </div>
                  </>
                )}

                <div className="mb-8 p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-800 flex items-center">
                    <svg className="w-6 h-6 mr-3" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"></path></svg>
                  <p className="text-sm">
                    Total Deposit Amount (after fees):{" "}
                    <span className="font-bold text-lg">
                      Ksh{" "}
                      {depositAmount !== null
                        ? parseFloat(depositAmount).toLocaleString('en-KE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                        : "0.00"}
                    </span>
                  </p>
                </div>

                <div className="flex justify-end">
                  <button
                    type="submit"
                    className="inline-flex items-center px-8 py-3 border border-transparent text-base font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition duration-300 ease-in-out transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <Oval
                        height={20}
                        width={20}
                        color="white"
                        visible={true}
                        ariaLabel="oval-loading"
                        secondaryColor="white"
                        strokeWidth={4}
                        strokeWidthSecondary={4}
                      />
                    ) : (
                      <>
                        <svg className="w-5 h-5 mr-2 -ml-1" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M4 4a2 2 0 00-2 2v6a2 2 0 002 2V6h10a2 2 0 00-2-2H4zm2 6a2 2 0 012-2h8a2 2 0 012 2v2a2 2 0 01-2 2H8a2 2 0 01-2-2v-2z"></path></svg>
                        Proceed to Deposit
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>

            {/* --- Fullscreen Iframe Modal --- */}
            {showIframe && webUrl && (
              <div className="fixed inset-0 bg-gray-900 bg-opacity-80 flex flex-col items-center justify-center p-4 z-50">
                <div className="relative w-full h-full max-w-4xl max-h-[90vh] bg-white rounded-lg shadow-xl overflow-hidden">
                  <button
                    onClick={handleCloseIframe}
                    className="absolute top-4 right-4 bg-red-600 hover:bg-red-700 text-white font-bold p-2 rounded-full focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 z-10"
                    aria-label="Close Payment Gateway"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path></svg>
                  </button>
                  <iframe
                    src={webUrl}
                    className="w-full h-full border-none"
                    title="Payment Gateway"
                    allowFullScreen
                  ></iframe>
                </div>
              </div>
            )}
          </div>
        </div>
      </AuthGuard>
    </RoleGuard>
  );
};

export default DepositsPage;
